import { SideBarSteps, SideBarIcon, ActiveIcon, CheckedIcon, SideBarSteppers } from './SideBarSteppers.styles';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { getSidebarPageOrder, pageNameToConfigIndex } from '@/helper/pageOrder';
import { useEffect, useRef, useState, memo } from 'react';
import type { FC } from 'react';
import { handleCurrentVmoPage } from '@/utils/handleCurrentPage';
import { selectIsGoingBack, selectStep, selectCurrentPageNumber, selectBackPage, setCurrentPage, setBackPage } from '@/reducers/currentPage';
import { useLocation } from 'react-router-dom';
import { DefaultModal } from '../Modals/DefaultModal/DefaultModal';

interface SideBarSteppersProps {
  variant: 'primary' | 'secondary' | 'default';
  color?: string;
}

const BarSteppersComponent: FC<SideBarSteppersProps> = ({ variant, color }) => {
  const currentStep = useAppSelector(selectStep);
  const currentPage = useAppSelector(selectCurrentPageNumber);
  const backPage = useAppSelector(selectBackPage);
  const completedSteps = useAppSelector((state) => state.currentPage.completedSteps);
  const dispatch = useAppDispatch();
  // const navigate = useNavigate();
  const location = useLocation();
  const isFirstRender = useRef(true);
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  const { isEnabled: isVmoRedesignEnabled = false } = templateConfig?.features?.vmoRedesign ?? {};
  const hasDrawPage = templateConfig?.pages?.hasDrawAmountPage ?? false;
  const hasDeclarations = !!templateConfig?.pages?.declarations;
  const isLpGateway = templateConfig?.isLpGateway ?? false;
  const pagesWithVmoVersion = handleCurrentVmoPage(isVmoRedesignEnabled, hasDrawPage, hasDeclarations);
  const pageName = pagesWithVmoVersion && pagesWithVmoVersion[currentStep];
  const isGoingBack = useAppSelector(selectIsGoingBack);
  const isVmoV1 = pageName === 'View My Offer!' && currentStep !== 3;
  const currentUrlPage = location.pathname;
  const isVmoV2 = templateConfig?.features?.vmoRedesign?.isEnabled && currentStep === 3;
  const isICE = templateConfig?.isICE === true;
  const isHelocProduct = loanApplication?.selectedProgramType === 'Line of Credit';
  const shouldShowDrawAmount = isICE ? hasDrawPage && isHelocProduct : hasDrawPage;
  const currentAmountPage = shouldShowDrawAmount ? '/vmo-draw-amount' : '/vmo-line-amount';
  const totalPageList = getSidebarPageOrder(hasDeclarations, isLpGateway);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const showModal = (msg: string) => {
    setModalMessage(msg);
    setIsModalOpen(true);
  };

  const pageRouteMap: { [key: string]: string } = {
    '/get-started': "Let's Get Started",
    '/estimated-available-equity': 'Estimated Available Equity',
    '/vmo': 'View My Offer',
    '/vmo-line-amount': 'View My Offer',
    '/vmo-draw-amount': 'View My Offer',
    '/personal-information': 'Personal Information',
    '/employment-information': 'Employment Information',
    '/government-monitoring-information': 'Demographic Information',
    '/declarations': 'Declarations',
    '/inquiry-summary': 'Inquiry Summary',
    '/completion': 'Completion',
  };

  const getActiveStepIndex = (): number => {
    const currentPageName = pageRouteMap[currentUrlPage];
    return totalPageList.findIndex((page) => page === currentPageName);
  };

  const isPageComplete = (pageIndex: number): boolean => {
    return completedSteps.includes(pageIndex);
  };

  const isOnCompletionPage = () => {
    const currentPageName = pageRouteMap[currentUrlPage];
    return currentPageName === 'Completion';
  };

  const getConfigPageIndex = (pageName: string): number | undefined => {
    const configIndex = pageNameToConfigIndex[pageName];
    if (configIndex === undefined) return undefined;

    const declarationsIndex = pageNameToConfigIndex.Declarations;
    if (!hasDeclarations && configIndex > declarationsIndex) {
      return configIndex - 1;
    }

    return configIndex;
  };

  const handleStepClick = (_index: number) => {
    // if (isOnCompletionPage()) {
    //   showModal('Navigation is disabled on the Completion page.');
    //   return;
    // }
    // const pageRoutes = Object.fromEntries(Object.entries(pageRouteMap).map(([route, name]) => [name, route]));
    // const targetPageName = totalPageList[index];
    // const route = pageRoutes[targetPageName as keyof typeof pageRoutes];
    // if (!route) return;
    // if (targetPageName === 'Completion') {
    //   showModal('Cannot navigate directly to the completion page.');
    //   return;
    // }
    // if (completedSteps.includes(index)) {
    //   dispatch(setCurrentPage(index));
    //   dispatch(setBackPage(currentPage));
    //   navigate(route);
    //   return;
    // }
    // if (index < currentPage) {
    //   dispatch(setCurrentPage(index));
    //   dispatch(setBackPage(currentPage));
    //   navigate(route);
    //   return;
    // }
    // let requiredStepIndex = index - 1;
    // while (requiredStepIndex >= 0 && completedSteps.includes(requiredStepIndex)) {
    //   requiredStepIndex--;
    // }
    // const requiredStepName = totalPageList[requiredStepIndex] || 'the previous step';
    // showModal(`You must complete the '${requiredStepName}' step before continuing.`);
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const nextStep = currentStep === 0 || (currentStep === 1 && currentStep > backPage) || isVmoV1 ? 0 : 1;
    dispatch(
      setCurrentPage(
        Math.max(0, isGoingBack ? (currentUrlPage === currentAmountPage || (!isVmoV1 && currentStep !== 3) || isVmoV2 ? currentPage - 1 : currentPage) : currentPage + nextStep),
      ),
    );
    dispatch(setBackPage(Math.max(0, currentStep)));
  }, [currentStep]);

  const activeStepIndex = getActiveStepIndex();

  return (
    <>
      <DefaultModal
        openModal={isModalOpen}
        setOpenModal={setIsModalOpen}
        infoModal={{
          header: 'Warning',
          body: modalMessage,
          button: 'Close',
        }}
        preventBackgroundInteraction={true}
      />
      <SideBarSteppers>
        {totalPageList.map((steps, index) => (
          <SideBarSteps
            key={index}
            $variant={variant}
            $color={color}
            $isCurrent={activeStepIndex === index && !isOnCompletionPage()}
            $isChecked={isPageComplete(index)}
            data-testid="side-bar-steps"
            onClick={isOnCompletionPage() ? undefined : () => handleStepClick(index)}
            style={{ cursor: isOnCompletionPage() ? 'not-allowed' : 'pointer' }}
          >
            <SideBarIcon>
              {activeStepIndex <= index && !isOnCompletionPage() ? (
                <ActiveIcon data-testid="active-icon" $isCurrent={activeStepIndex === index && !isOnCompletionPage()} />
              ) : (
                <CheckedIcon data-testid="checked-icon" $isCurrent={activeStepIndex === index && !isOnCompletionPage()} />
              )}
            </SideBarIcon>
            {templateConfig?.pages?.pages?.[getConfigPageIndex(steps) ?? -1] ?? steps}
          </SideBarSteps>
        ))}
      </SideBarSteppers>
    </>
  );
};
export const BarSteppers = memo(BarSteppersComponent);
